import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { SENTRY_DSN, SEGMENT_ENV } from 'config';
import type { LoginContext } from 'features/auth';

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: APP_VERSION || 'unknown',
    environment: SEGMENT_ENV,
    replaysSessionSampleRate: 0.5,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0.02,
    // Auth0's custom domains (auth.<env>.delogue.com / .asia) must NOT be trace-propagation
    // targets: browserTracing would attach `sentry-trace` + `baggage`, and Auth0 answers the
    // CORS preflight with a FIXED Access-Control-Allow-Headers list (Origin, Content-Type,
    // Accept, X-Requested-With, Authorization, Auth0-Client, X-Request-Language, DPoP) that it
    // does not extend on request — so the /oauth/token exchange is blocked and federated login
    // dies in a redirect loop. There is no Auth0 setting to allow extra request headers, and
    // tracePropagationTargets has no exclude list, hence the negative lookahead (DL-63995).
    // Anchoring at the origin also stops a bare `delogue.com` substring in a third-party URL's
    // path or query string from qualifying, which the previous patterns allowed. The host is
    // matched label by label so a suffix-sharing origin (evil-delogue.com, notdelogue.com) and a
    // deeper-suffix one (delogue.com.evil.com) are excluded too, and the trailing lookahead keeps
    // the port form (delogue.com:8443) matching.
    tracePropagationTargets: [
      /^https?:\/\/(?!auth\.)([a-z0-9-]+\.)*delogue\.(com|asia)(?=[:/?#]|$)/i,
      /localhost/,
    ],
    integrations: [
      Sentry.replayIntegration({
        networkDetailAllowUrls: [/.*/],
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        networkRequestHeaders: ['cf-ray', 'content-type', 'server-timing'],
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    beforeSend(event, hint) {
      // RC3: Drop events whose most-recent XHR breadcrumb is a 4xx status.
      // Rationale: errors surfacing immediately after a 4xx are almost always a direct consequence
      // of the 4xx (e.g. accessing .data on a failed response). The app redirects to login on 401,
      // so there is no meaningful session activity after that point anyway.
      // Trade-off 1: a genuine JS bug (e.g. TypeError in an unrelated component) firing within the
      // same tick as a background 4xx would be dropped. Accepted; 400/403/404 filtering was already
      // in place before this PR — 401 follows the same rationale.
      // Trade-off 2: dropping 401s means frontend Sentry loses visibility into auth token/SSO regressions.
      // Accepted because auth 401s are still visible in legacy-backend Sentry (server-side) and in
      // any auth-specific monitoring. If a systematic 401 flood occurs, investigate backend Sentry first.
      // Mirror of init.js beforeSend (Src/DesignHub.WebMvc/js/tracking/init.js) — keep both in sync.
      const breadcrumbs = event?.breadcrumbs ?? [];
      for (let i = breadcrumbs.length - 1; i >= 0; i--) {
        const crumb = breadcrumbs[i];
        if (crumb?.category !== 'xhr' || !crumb.data) continue;
        const status = parseInt(String(crumb.data.status_code ?? ''), 10);
        if (Number.isNaN(status)) continue; // malformed status_code — skip and keep searching
        if (status === 400 || status === 401 || status === 403 || status === 404) {
          return null;
        }
        break; // First well-formed XHR breadcrumb found; stop searching
      }

      const error = hint.originalException;

      // RC1: SignalR rejects its Promise with a plain string in some code paths,
      // bypassing the typeof === 'object' guard below — handle strings first.
      if (typeof error === 'string') {
        const strLower = error.toLowerCase();
        if (
          strLower.includes('cannot send data if the connection is not in the') ||
          strLower.includes('server timeout elapsed') ||
          strLower.includes('invocation canceled')
        ) {
          return null;
        }
      }

      if (error && typeof error === 'object') {
        const stackTrace = ('stack' in error ? String(error.stack) : '').toLowerCase();
        const message =
          'message' in error && typeof error.message === 'string' ? error.message : '';
        const msgLower = message.toLowerCase();

        // RC1: always drop this exact SignalR client error
        if (msgLower.includes('cannot send data if the connection is not in the')) {
          return null;
        }
        // RC2: drop SignalR keepalive timeout and connection-closed cancellation
        if (msgLower.includes('server timeout elapsed without receiving a message')) {
          return null;
        }
        if (msgLower.includes('invocation canceled due to the underlying connection')) {
          return null;
        }
        // Stack-based: only drop when BOTH stack suggests SignalR AND message suggests connection error
        // (avoids dropping app bugs inside SignalR callbacks, e.g. "Cannot read property of undefined")
        if (
          (stackTrace.includes('hubconnection') || stackTrace.includes('signalr')) &&
          (msgLower.includes('connection') ||
            msgLower.includes('connect') ||
            msgLower.includes('disconnect') ||
            msgLower.includes('websocket') ||
            msgLower.includes('transport') ||
            msgLower.includes('closed'))
        ) {
          return null;
        }
      }
      return event;
    },
    ignoreSpans: [
      // Browser connection events
      { op: /^browser\.(cache|connect|DNS)$/ },
      // Fonts
      { op: 'resource.other', name: /.+\.(woff2|woff|ttf|eot)$/ },
      // CSS files
      { op: 'resource.link', name: /.+\.css.*$/ },
      // JS files
      { op: /resource\.(link|script)/, name: /.+\.js.*$/ },
      // Images
      {
        op: /resource\.(other|img)/,
        name: /.+\.(png|svg|jpe?g|gif|bmp|tiff?|webp|avif|heic?|ico).*$/,
      },
      // Measure spans
      { op: 'measure' },
    ],
  });
}

export const setSentryUser = (u: LoginContext) => {
  if (!u) return;

  Sentry.setUser({
    id: `${SEGMENT_ENV}:${u.id}`,
    email: u.email,
    username: u.name,
  });
  Sentry.setTags({
    organizationId: `${SEGMENT_ENV}:${u.organizationId.toString()}`,
    organizationName: u.organizationName,
    organizationType: u.organizationType === 1 ? 'Designer' : 'Supplier',
    userRoles: u.roleNames.join(','),
    userPlan: u.planType,
    userPlanTier: u.planTier,
    userSignupDate: u.signUpDate
      ? (() => {
          const parsed = parseInt(u.signUpDate, 10);
          if (!isNaN(parsed) && parsed > 0) {
            return new Date(parsed).toISOString().replace(/\.\d{3}Z$/, '.000Z');
          }
          return '';
        })()
      : '', // Necessary to match C# toString("o")
  });
};
